import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { SectionErrorBoundary } from './SectionErrorBoundary';
import { AIGate } from './AIGate';
import BrainNotificationListener from './Brain/BrainNotificationListener';
import GlobalCreditListener from './GlobalCreditListener';
import DocsCommandPalette from './DocsCommandPalette';
import api from '../lib/api';

import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  LayoutDashboard,
  Users,
  UserCircle,
  FileText,
  FileSpreadsheet,
  Calendar,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  CreditCard,
  ShoppingBag,
  ShoppingCart,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Layers,
  FileInput,
  FileOutput,
  DollarSign,
  Lock,
  Code,
  Plus,
  Wrench,
  Bot,
  Activity,
  Bell,
  Database,
  PhoneForwarded,
  Phone,
  Shield,
  Download,
  Brain,
  PanelLeftClose,
  PanelLeft,
  Sparkles,
  Zap,
  UserPlus,
  CircleUser,
  Palette,
  CheckCircle2,
  Eye,
  Trash2,
  ExternalLink,
  Plug,
  FileDown,
  MessageCircle,
  Headset,
  Building2,
  Coins,
  ShieldAlert,
  Target,
  Mail,
  TrendingUp,
  Lightbulb,
  CalendarCheck,
  Send,
  Briefcase,
  MapPin,
  Braces,
  Workflow,
  RefreshCw,
  Package,
  Linkedin,
  Chrome,
  Inbox,
  FolderOpen,
  ClipboardCheck,
  AlertTriangle,
  PhoneIncoming,
  Calculator,
  HelpCircle,
  RotateCcw,
  BookOpen,
  Search,
} from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { NotificationPanel } from './NotificationPanel';
import FirstLoginTour from './FirstLoginTour';
import { MobileSidebarNav, DesktopSidebarNav, SidebarCollapseButton } from './SidebarNav';
import { ActivationBanner } from './dashboard/ActivationBanner';

const navigation = [
  // 1. Dashboard (single link - tabs managed within dashboard)
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  
  // 2. Schedule
  { 
    name: 'Schedule', 
    icon: Calendar,
    submenu: [
      { name: 'Calendar', href: '/schedule', icon: Calendar },
      { name: 'All Events', href: '/schedule/events', icon: Calendar },
      { name: 'Tasks', href: '/schedule/tasks', icon: FileText },
      { name: 'Follow-Up', href: '/schedule/followups', icon: Users },
      { name: 'Visits', href: '/schedule/visits', icon: Briefcase },
      { name: 'Meetings', href: '/schedule/meetings', icon: Users },
    ]
  },
  
  // 3. Client
  { name: 'Client', href: '/clients', icon: UserCircle },
  
  // ── DISCOVER ─────────────────────────────────
  
  // 3.5 Discover Leads (top-level — high-value feature)
  { name: 'Discover Leads', href: '/discover-leads', icon: Sparkles },

  // ── CORE CRM ──────────────────────────────────
  
  // 4. Sales (Lead Pipeline + Intelligence)
  { 
    name: 'Sales', 
    icon: Users,
    submenu: [
      { name: 'Lead Kanban', href: '/leads', icon: Users },
      { name: 'Deals', href: '/deals', icon: Briefcase },
      { name: 'Lead Setting', href: '/leads-settings', icon: Settings },
      { name: 'ICP Profile', href: '/brain/icp', icon: Target, adminOnly: true },
      { name: 'ICP Learning', href: '/brain/icp-learning', icon: TrendingUp, adminOnly: true },
      { name: 'Brain Insights', href: '/brain/insights', icon: Lightbulb, adminOnly: true },
      { name: 'Brain Alerts', href: '/brain/alerts', icon: ShieldAlert, adminOnly: true, badgeKey: 'brainAlerts' },
      { name: 'Reveal Analytics', href: '/ai-agents/reveal-analytics', icon: Eye, adminOnly: true },
    ]
  },
  
  // 5. Outreach & Engagement
  { 
    name: 'Outreach', 
    icon: Send,
    adminOnly: true,
    submenu: [
      // ── Campaigns & Automation
      { name: 'Audience', href: '/audience', icon: Users, adminOnly: true, separator: 'Campaigns & Automation' },
      { name: 'Campaigns', href: '/campaigns', icon: Target, adminOnly: true },
      { name: 'AI Workflows', href: '/ai-workflows', icon: Workflow, adminOnly: true },
      // ── Email
      { name: 'Email Outbox', href: '/brain/email-outbox', icon: Mail, adminOnly: true, minPlan: 'starter', separator: 'Email' },
      // ── Voice
      { name: 'Voice AI', href: '/ai-agents/voice-settings', icon: PhoneForwarded, adminOnly: true, minPlan: 'growth', separator: 'Voice' },
      { name: 'Inbound Voice', href: '/ai-agents/inbound-voice', icon: PhoneIncoming, adminOnly: true, minPlan: 'growth' },
      { name: 'Voice Call Flows', href: '/brain/voice-flows', icon: PhoneForwarded, adminOnly: true, minPlan: 'growth' },
      { name: 'Call History', href: '/ai-agents/call-history', icon: Phone, adminOnly: true, minPlan: 'growth' },
      // ── Social
      { name: 'Social Connectors', href: '/social-connectors', icon: Plug, adminOnly: true, separator: 'Social' },
      { name: 'LinkedIn Outbox', href: '/linkedin-outbox', icon: Linkedin, adminOnly: true, minPlan: 'growth' },
      { name: 'LinkedIn Extension', href: '/linkedin-extension', icon: Chrome, adminOnly: true, minPlan: 'growth' },
      { name: 'WhatsApp', href: '/whatsapp', icon: MessageCircle, adminOnly: true, minPlan: 'growth' },
      { name: 'Live Chat', href: '/live-conversations', icon: Headset, adminOnly: true },
      { name: 'Response Inbox', href: '/responses', icon: Inbox, adminOnly: true, badgeKey: 'responseInbox' },
      // ── Tools
      { name: 'Outreach Pipeline', href: '/reports/outreach', icon: Send, adminOnly: true, separator: 'Tools' },
      { name: 'Document Library', href: '/document-library', icon: FolderOpen, adminOnly: true },
      { name: 'Doc Verification', href: '/doc-verification', icon: ClipboardCheck, adminOnly: true },
      { name: 'Meeting Scheduler', href: '/ai-agents/meeting-scheduler', icon: CalendarCheck, adminOnly: true },
    ]
  },
  
  // 6. Account
  { 
    name: 'Account', 
    icon: FileText,
    submenu: [
      { name: 'Quote', href: '/quotes', icon: FileText },
      { name: 'Invoice', href: '/invoices', icon: FileSpreadsheet },
      { name: 'Payments', href: '/payments', icon: DollarSign },
      { name: 'Taxes', href: '/taxes', icon: DollarSign },
      { name: 'Quote Status Settings', href: '/account/quote-status-settings', icon: Settings },
      { name: 'Invoice Status Settings', href: '/account/invoice-status-settings', icon: Settings },
    ]
  },
  
  // ── INTELLIGENCE ────────────────────────────────
  
  // 8. AI Brain (core intelligence & system-level controls)
  { 
    name: 'AI Brain', 
    icon: Brain,
    adminOnly: true,
    submenu: [
      { name: 'Command Center', href: '/brain/command-center', icon: Activity, adminOnly: true },
      { name: 'Brain Digest', href: '/brain/digest', icon: Mail, adminOnly: true },
      { name: 'Knowledge Base', href: '/ai-agents/knowledge-base', icon: Database, adminOnly: true },
      { name: 'Breach Management', href: '/ai-agents/breach-management', icon: ShieldAlert, adminOnly: true },
    ]
  },
  
  // 9. AI Templates
  { name: 'AI Templates', href: '/brain/templates', icon: ShoppingBag, adminOnly: true },
  
  // ── REPORTS & ANALYTICS ─────────────────────────
  
  // 10. Reports
  { 
    name: 'Reports', 
    icon: BarChart3,
    submenu: [
      { name: 'Sales Reports', href: '/reports/sales', icon: BarChart3 },
      { name: 'Engagement Reports', href: '/reports/engagement', icon: Mail },
      { name: 'LEO Impact (ROI)', href: '/reports/leo-impact', icon: TrendingUp, adminOnly: true },
      { name: 'Finance Reports', href: '/reports/finance', icon: FileSpreadsheet },
    ]
  },
  
  // ── TEAM & ADMIN ────────────────────────────────
  
  // 12. Billing & Plans
  { 
    name: 'Billing & Plans', 
    href: '/billing',
    icon: CreditCard,
  },
  
  // ── SYSTEM ──────────────────────────────────────
  
  // 13. Settings (consolidated)
  { 
    name: 'Setting', 
    icon: Settings,
    submenu: [
      { name: 'Profile', href: '/profile', icon: UserCircle },
      { name: 'CRM Sync', href: '/settings/crm-sync-v2', icon: RefreshCw, adminOnly: true, minPlan: 'scale' },
      { name: 'Territories', href: '/settings/territories', icon: MapPin, adminOnly: true, minPlan: 'scale' },
      { name: 'SLA Rules', href: '/settings/sla-rules', icon: ShieldAlert, adminOnly: true },
      { name: 'Security', href: '/settings/security', icon: Shield, adminOnly: true },
      { name: 'Deal Approvals', href: '/settings/deal-approvals', icon: ShieldAlert, adminOnly: true },
      { name: 'CPQ Pricing', href: '/settings/cpq', icon: Workflow, adminOnly: true },
      { name: 'Commissions', href: '/settings/commissions', icon: DollarSign, adminOnly: true },
      { name: 'Integrations', href: '/integrations', icon: Plug, adminOnly: true },
      { name: 'API Keys', href: '/developer/api-keys', icon: Lock, adminOnly: true },
    ]
  },
];

// Compact credit balance for header bar (Unit Credits: 1 USD = 100 Credits)
// Iter 37: shared 30s cache so CreditHeaderBalance + CreditsLowBanner + WS update
// don't trigger a /credits/summary storm on every navigation.
async function _fetchCreditsSummaryCached() {
  if (!window.__credits_cache) window.__credits_cache = { at: 0, data: null, inflight: null };
  const cache = window.__credits_cache;
  const now = Date.now();
  if (cache.data && (now - cache.at) < 30_000) return cache.data;
  if (cache.inflight) return cache.inflight;
  cache.inflight = api.get('/api/tenant/billing/credits/summary')
    .then(r => { cache.data = r.data; cache.at = Date.now(); return r.data; })
    .catch(() => null)
    .finally(() => { cache.inflight = null; });
  return cache.inflight;
}

function CreditHeaderBalance() {
  const [balanceCredits, setBalanceCredits] = React.useState(null);
  const [creditsLow, setCreditsLow] = React.useState(false);
  React.useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    _fetchCreditsSummaryCached().then(d => {
      if (!d) return;
      setBalanceCredits(d.balance_credits ?? Math.round((d.balance || 0) * 100));
      setCreditsLow(d.credits_low || false);
    });
  }, []);
  if (balanceCredits === null) return <span className="text-xs text-amber-600">Credits</span>;
  return (
    <span className={`text-xs font-semibold ${creditsLow ? 'text-red-600 dark:text-red-400 animate-pulse' : 'text-amber-700 dark:text-amber-300'}`}>
      {creditsLow && '! '}{balanceCredits.toLocaleString()} Credits
    </span>
  );
}

// Credits Running Low banner — shows at top of app when balance < 20% of included credits
function CreditsLowBanner() {
  const [show, setShow] = React.useState(false);
  const [dismissed, setDismissed] = React.useState(false);

  React.useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    _fetchCreditsSummaryCached().then(d => {
      if (d?.credits_low) setShow(true);
    });
  }, []);

  if (!show || dismissed) return null;

  return (
    <div data-testid="credits-low-banner" className="bg-red-50 dark:bg-red-950/40 border-b border-red-200 dark:border-red-800 px-4 py-2 flex items-center justify-between text-sm">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0" />
        <span className="text-red-800 dark:text-red-300">
          <strong>Credits running low.</strong> AI features may be limited. 
          <a href="/billing?tab=credits" className="underline ml-1 font-medium hover:text-red-900">Add balance now</a>
        </span>
      </div>
      <button 
        onClick={() => setDismissed(true)} 
        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200 ml-4"
        data-testid="credits-low-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// DEMO MODE banner — shown for demo tenants. Includes the "allow live actions" toggle.
function DemoModeBanner() {
  const [state, setState] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    api.get('/api/ai/demo/self').then(r => {
      if (r?.data?.is_demo) setState(r.data);
    }).catch(() => {});
  }, []);

  if (!state?.is_demo) return null;

  const toggleLive = async () => {
    setBusy(true);
    try {
      const next = !state.allow_live;
      const r = await api.post('/api/ai/demo/self/allow-live', { allow_live: next });
      setState(s => ({ ...s, allow_live: r?.data?.allow_live ?? next, live_allowed: r?.data?.allow_live ?? next }));
    } catch (e) { /* noop */ }
    setBusy(false);
  };

  const switchRegion = async (region) => {
    if (region === state.region || busy) return;
    setBusy(true);
    try {
      await api.post('/api/ai/demo/self/region', { region });
      // Reseeding wipes + recreates all leads/invoices with NEW ids, so the
      // current URL (e.g. /leads/{old-id}) would 404. Land on a stable list page
      // and force a full reload so AuthContext refetches the user (new currency).
      window.location.assign('/leads');
    } catch (e) { setBusy(false); }
  };

  return (
    <div data-testid="demo-mode-banner" className="bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-800 px-4 py-2 flex items-center justify-between gap-3 text-sm flex-wrap">
      <div className="flex items-center gap-2 min-w-0">
        <Sparkles className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
        <span className="text-amber-800 dark:text-amber-300 truncate">
          <strong>Demo Mode</strong>
          {state.region_chip && <span className="ml-2 hidden sm:inline px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/50 border border-amber-300 dark:border-amber-700 text-xs font-medium" data-testid="demo-region-chip">{state.region_flag} {state.region_chip}</span>}
          {state.allow_live
            ? <span className="ml-2 font-medium text-red-600 dark:text-red-400">Live ON</span>
            : <span className="ml-2 hidden md:inline">· simulated</span>}
        </span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="text-xs text-amber-700 dark:text-amber-400 hidden sm:inline">Region:</span>
        <select
          value={state.region}
          onChange={(e) => switchRegion(e.target.value)}
          disabled={busy}
          data-testid="demo-region-select"
          className="text-xs rounded-md border border-amber-300 dark:border-amber-700 bg-white dark:bg-amber-950 px-2 py-1 text-amber-900 dark:text-amber-200 focus:outline-none">
          {(state.available_regions || []).map(r => (
            <option key={r.code} value={r.code}>{r.flag} {r.label} ({r.currency})</option>
          ))}
        </select>
        <button
          onClick={toggleLive}
          disabled={busy}
          data-testid="demo-allow-live-toggle"
          className={`text-xs font-semibold px-3 py-1 rounded-full border transition-colors ${state.allow_live
            ? 'bg-red-100 dark:bg-red-900/40 border-red-300 dark:border-red-700 text-red-700 dark:text-red-300'
            : 'bg-amber-100 dark:bg-amber-900/40 border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300'}`}>
          {busy ? '…' : (state.allow_live ? 'Disable live' : 'Allow live')}
        </button>
      </div>
    </div>
  );
}


export const AppShell = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const { isDark: darkMode, toggle: toggleDarkMode } = useTheme();
  const [expandedMenus, setExpandedMenus] = useState({});
  const [flyoutPos, setFlyoutPos] = useState({ top: 0, left: 0 });
  const [activeMenu, setActiveMenu] = useState(null); // Track which menu tab is currently active (clicked)
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoaded, setSubscriptionLoaded] = useState(false);
  const [tenantModules] = useState(['sales', 'accounts']);
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false); // Notification off-canvas
  const [notifications, setNotifications] = useState({ unread: [], read: [] });
  const [notificationCounts, setNotificationCounts] = useState({ unread: 0, total: 0 });
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  // Onboarding state
  const [onboardingSkipped, setOnboardingSkipped] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState(true);
  const [hasCreatedLead, setHasCreatedLead] = useState(false);
  const [profileSetupCompleted, setProfileSetupCompleted] = useState(false);
  const [primaryModuleSetupDone, setPrimaryModuleSetupDone] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [brainAlertCount, setBrainAlertCount] = useState(0);
  const [responseInboxCount, setResponseInboxCount] = useState(0);
  const [icpReady, setIcpReady] = useState(true); // Phase A: optimistically true; flipped false during onboarding wait
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { hasModuleAccess, hasPermission, isAdmin } = usePermissions();

  // Toggle sidebar collapsed state and save to localStorage
  const toggleSidebarCollapse = () => {
    setSidebarCollapsed(prev => {
      const newValue = !prev;
      localStorage.setItem('sidebarCollapsed', JSON.stringify(newValue));
      return newValue;
    });
  };

  // Map navigation items to module IDs for permission checking AND subscription checking
  const moduleMap = {
    'Dashboard': 'all',
    'Overview': 'all',
    'Schedule': 'all',
    'Client': 'all',
    'Sales': 'lead',
    'Discover Leads': 'lead',
    'Account': 'account',
    'Reports': 'report',
    'Setting': 'all',
    // Sub-menu items
    'Calendar': 'all',
    'All Events': 'all',
    'Tasks': 'all',
    'Follow-Up': 'all',
    'Visits': 'all',
    'Lead Kanban': 'lead',
    'Deals': 'lead',
    'Lead Setting': 'lead',
    'Quote': 'account',
    'Invoice': 'account',
    'Payments': 'account',
    'Taxes': 'account',
    'Quote Status Settings': 'account',
    'Invoice Status Settings': 'account',
    'Users': 'hr',
    'Accounts': 'account',
    'Client Report': 'report',
    'Lead Report': 'report',
    'Quote Report': 'report',
    'Invoice Report': 'report',
    'Billing & Plans': 'all',
    'Inbound Voice': 'all',
    'Profile': 'all',
    'About Us': 'all',
    'Contact us': 'all',
    'Privacy Policy': 'all',
    'Terms & Conditions': 'all',
    'AI Settings': 'all',  // Visibility controlled by adminOnly flag
    'Knowledge Base': 'all',  // Admin only - controlled by adminOnly flag
    'Voice AI Settings': 'all',  // Admin only - controlled by adminOnly flag
    'Voice AI Analytics': 'all',  // Admin only - controlled by adminOnly flag
    'Call History': 'all',  // Admin only - controlled by adminOnly flag
    'AI Activity Feed': 'all',  // Admin only - controlled by adminOnly flag
    'Enterprise Controls': 'all',  // Admin only - controlled by adminOnly flag
    'Compliance': 'all',  // Admin only - controlled by adminOnly flag
    'Exports & Reporting': 'all',  // Admin only - controlled by adminOnly flag
    'Outreach & Engagement': 'all',  // Admin only - controlled by adminOnly flag
    'AI Brain': 'all',  // Admin only - controlled by adminOnly flag
    'ICP Profile': 'lead',
    'ICP Learning': 'lead',
    'Brain Insights': 'lead',
    'Brain Alerts': 'lead',
    'Cadence Analytics': 'lead',
    'Reveal Analytics': 'lead',
    'Email Outbox': 'all',
    'Document Library': 'all',
    'Doc Verification': 'all',
    'Email Engagement': 'all',
    'Voice Call Flows': 'all',
    'Voice AI': 'all',
    'Meeting Scheduler': 'all',
    'Command Center': 'all',
    'Brain Digest': 'all',
    'Activity Feed': 'all',
    'Governance': 'all',
    'API Keys': 'all',
    'Documentation': 'all',
  };

  // Map submenu items to their specific module and submodule for granular permission checking
  const submoduleMap = {
    // Account submenus
    'Quote': { module: 'account', submodule: 'quote' },
    'Invoice': { module: 'account', submodule: 'invoice' },
    'Payments': { module: 'account', submodule: 'payment' },
    'Taxes': { module: 'account', submodule: 'multiple_tax' },
    'Quote Status Settings': { module: 'account', submodule: 'quote' },
    'Invoice Status Settings': { module: 'account', submodule: 'invoice' },
    // HR submenus
    'Users': { module: 'hr', submodule: 'user' },
    // Lead submenus
    'Lead Kanban': { module: 'lead', submodule: 'kanban' },
    'Lead Setting': { module: 'lead', submodule: 'kanban' },
  };

  // Helper function to check submenu item permission (memoized to prevent infinite loops)
  const hasSubmenuPermission = useCallback((itemName) => {
    const mapping = submoduleMap[itemName];
    if (!mapping) return true; // No specific submodule mapping, use module-level access
    
    // Check if user has at least view permission for this submodule
    return hasPermission(mapping.module, mapping.submodule, 'view');
  }, [hasPermission]);

  // Load subscription status
  useEffect(() => {
    loadSubscription();
    loadOnboardingState();
    loadBrainAlertCount();
  }, []);

  // Load onboarding state (modules always default to all 3)
  const loadOnboardingState = async () => {
    try {
      const response = await api.get('/api/onboarding/status');
      const data = response.data;
      setOnboardingSkipped(data.onboarding_skipped || false);
      setOnboardingCompleted(data.onboarding_completed || false);
      setHasCreatedLead(data.has_created_lead || false);
      setProfileSetupCompleted(data.profile_setup_completed || false);
      setPrimaryModuleSetupDone(data.primary_module_setup_done || false);
      // Phase A: surface "Preparing AI" badges on AI-dependent menus until ICP lands
      setIcpReady(!!(data.icp_ready || data.brain_data_seeded));
    } catch (error) {
      console.error('[AppShell] Failed to load onboarding state:', error);
    }
  };

  // Load brain alert unread count for sidebar badge
  const loadBrainAlertCount = async () => {
    try {
      const res = await api.get('/api/brain/alerts?read=false&limit=1');
      setBrainAlertCount(res.data?.counts?.unread || 0);
    } catch {
      // Silently fail — badge just won't show
    }
  };

  // Load Response Inbox waiting-reply count for the sidebar badge (A)
  const loadResponseInboxCount = async () => {
    try {
      const res = await api.get('/api/responses/count');
      setResponseInboxCount((res.data?.counts?.open || 0) + (res.data?.counts?.unmatched || 0));
    } catch {
      // Silently fail — badge just won't show
    }
  };

  // Refresh sidebar counts periodically (every 60s)
  useEffect(() => {
    loadResponseInboxCount();
    const interval = setInterval(() => { loadBrainAlertCount(); loadResponseInboxCount(); }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Phase A: poll icp_ready every 4s while it's false, so the "Preparing AI"
  // badges flip off the moment the worker completes ICP generation.
  useEffect(() => {
    if (icpReady) return;
    const interval = setInterval(loadOnboardingState, 4000);
    return () => clearInterval(interval);
  }, [icpReady]);

  // Handle resume onboarding
  const handleResumeOnboarding = async () => {
    try {
      await api.post('/api/onboarding/resume');
      setOnboardingSkipped(false);
      navigate('/onboarding/brain-init');
    } catch (error) {
      console.error('[AppShell] Failed to resume onboarding:', error);
    }
  };

  const loadSubscription = async () => {
    try {
      const response = await api.get('/api/subscriptions/current');
      const data = response.data;
      console.log('[AppShell] Subscription loaded:', data);
      setSubscription(data);
      setSubscriptionLoaded(true);
    } catch (error) {
      console.error('[AppShell] Failed to load subscription:', error);
      setSubscriptionLoaded(true);
    }
  };

  // Load notifications
  const loadNotifications = useCallback(async () => {
    try {
      setNotificationsLoading(true);
      
      const [unreadRes, readRes, countsRes] = await Promise.all([
        api.get('/api/app-notifications?is_read=false&limit=50').catch(() => ({ data: {} })),
        api.get('/api/app-notifications?is_read=true&limit=50').catch(() => ({ data: {} })),
        api.get('/api/app-notifications/counts').catch(() => ({ data: {} })),
      ]);
      
      setNotifications({
        unread: unreadRes.data?.notifications || [],
        read: readRes.data?.notifications || []
      });
      
      if (countsRes.data) {
        setNotificationCounts(countsRes.data);
      }
    } catch (error) {
      console.error('[AppShell] Failed to load notifications:', error);
    } finally {
      setNotificationsLoading(false);
    }
  }, []);

  // Load notifications when panel opens
  useEffect(() => {
    if (notificationPanelOpen) {
      loadNotifications();
    }
  }, [notificationPanelOpen, loadNotifications]);

  // Also load notification counts on initial load and periodically
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const response = await api.get('/api/app-notifications/counts');
        setNotificationCounts(response.data);
      } catch (error) {
        // Silently fail
      }
    };
    
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/api/app-notifications/${notificationId}/read`);
      loadNotifications();
    } catch (error) {
      console.error('[AppShell] Failed to mark notification as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await api.post('/api/app-notifications/mark-all-read');
      loadNotifications();
    } catch (error) {
      console.error('[AppShell] Failed to mark all notifications as read:', error);
    }
  };

  // Format notification time
  const formatNotificationTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString();
  };

  // Get icon for notification category
  const getNotificationIcon = (category) => {
    const icons = {
      lead: Users,
      client: UserCircle,
      invoice: FileSpreadsheet,
      quote: FileText,
      user: Users,
      schedule: Calendar,
      billing: CreditCard,
      system: Settings,
    };
    return icons[category] || Bell;
  };

  // Check if module is accessible based on subscription
  const canAccessModule = (moduleId) => {
    // Always show universal modules
    if (moduleId === 'all') return true;
    
    // If subscription not loaded yet, show modules (optimistic)
    if (!subscriptionLoaded) return true;
    
    // If no subscription data, allow access (fallback)
    if (!subscription) return true;
    
    // Trial has full access
    if (subscription.status === 'trial') return true;
    
    // If subscription not active, deny access
    if (subscription.status !== 'active') return false;
    
    // Check features
    const features = subscription.features || {};
    
    // Professional/Business plans have all modules
    if (features.modules === 'all') return true;
    
    // Starter plan - only selected module
    if (features.modules === 1) {
      return moduleId === subscription.selected_module;
    }
    
    // Default: allow access if features exist
    return true;
  };

  // Map sidebar module names to onboarding module IDs
  // All 3 core modules are always enabled
  const isModuleEnabledByOnboarding = () => true;

  // Filter navigation based on permissions AND subscription AND onboarding modules
  const filteredNavigation = useMemo(() => {
    console.log('[AppShell] Filtering navigation...');
    console.log('[AppShell] User:', user);
    console.log('[AppShell] Subscription:', subscription);
    console.log('[AppShell] Tenant Modules:', tenantModules);
    console.log('[AppShell] isAdmin:', isAdmin());
    
    // Plan hierarchy for feature gating
    const planHierarchy = ['free', 'starter', 'growth', 'scale', 'enterprise'];
    const currentPlanId = subscription?.plan_id || user?.plan_id || 'free';
    const currentPlanLevel = planHierarchy.indexOf(currentPlanId);
    
    const meetsMinPlan = (minPlan) => {
      if (!minPlan) return true;
      const requiredLevel = planHierarchy.indexOf(minPlan);
      return currentPlanLevel >= requiredLevel;
    };
    
    // Helper to check if user can see admin-only items
    const canSeeAdminOnly = () => {
      // tenant_admin and org_admin can see admin-only items
      return user?.role === 'tenant_admin' || user?.role === 'org_admin' || isAdmin();
    };
    
    // Helper to filter submenu items (handles adminOnly flag + plan gating)
    const filterSubmenuItems = (submenu, checkPermissions = false) => {
      return submenu.map(subItem => {
        // Check adminOnly flag first
        if (subItem.adminOnly && !canSeeAdminOnly()) {
          return null;
        }
        // If checking permissions, also check submodule permission
        if (checkPermissions) {
          const hasSubPerm = hasSubmenuPermission(subItem.name);
          if (!hasSubPerm) return null;
        }
        // Check plan gating - show but mark as locked
        if (subItem.minPlan && !meetsMinPlan(subItem.minPlan)) {
          return { ...subItem, locked: true, requiredPlan: subItem.minPlan };
        }
        return subItem;
      }).filter(item => item !== null);
    };
    
    // System admins (no module_permissions) need to check subscription AND onboarding modules
    if (isAdmin()) {
      console.log('[AppShell] User is ADMIN - checking subscription and onboarding access');
      return navigation.map(item => {
        const moduleId = moduleMap[item.name];
        
        // Check if module is enabled by onboarding selection
        if (!isModuleEnabledByOnboarding(item.name)) {
          console.log('[AppShell] Admin - Hiding item not selected in onboarding:', item.name);
          return null;
        }
        
        // Filter submenus for admin-only items
        if (item.submenu) {
          const filteredSubmenu = filterSubmenuItems(item.submenu, false);
          if (filteredSubmenu.length === 0) return null;
          return { ...item, submenu: filteredSubmenu };
        }
        
        // Always show universal modules
        if (moduleId === 'all') return item;
        
        // Check subscription access for module-specific items
        const hasSubscription = canAccessModule(moduleId);
        console.log('[AppShell] Admin - Item:', item.name, 'moduleId:', moduleId, 'hasSubscription:', hasSubscription);
        return hasSubscription ? item : null;
      }).filter(item => item !== null);
    }
    
    // Regular users with permissions - filter based on BOTH permissions AND subscription AND onboarding modules
    if (user && user.module_permissions) {
      console.log('[AppShell] Regular user with permissions - filtering modules');
      const filtered = navigation.map(item => {
        // Hide entire Subscription menu from team users
        if (item.name === 'Subscription') {
          console.log('[AppShell] Hiding Subscription menu from team user');
          return null;
        }
        
        // Check if module is enabled by onboarding selection
        if (!isModuleEnabledByOnboarding(item.name)) {
          console.log('[AppShell] Hiding item not selected in onboarding:', item.name);
          return null;
        }
        
        // For items with submenus, filter submenus first based on submodule permissions AND adminOnly
        if (item.submenu) {
          const filteredSubmenu = filterSubmenuItems(item.submenu, true);
          
          // If no submenus visible, hide the parent
          if (filteredSubmenu.length === 0) {
            console.log('[AppShell] Hiding parent menu with no visible submenus:', item.name);
            return null;
          }
          
          return { ...item, submenu: filteredSubmenu };
        }
        
        // For items without submenus, check module access
        const moduleId = moduleMap[item.name];
        
        // Show to all users
        if (moduleId === 'all') {
          console.log('[AppShell] Item:', item.name, '- Show to all (moduleId=all)');
          return item;
        }
        
        // Check permission only (subscription inherited from admin)
        const hasModulePerm = hasModuleAccess(moduleId);
        console.log('[AppShell] Item:', item.name, '- hasPermission:', hasModulePerm);
        
        return hasModulePerm ? item : null;
      }).filter(item => item !== null);
      
      console.log('[AppShell] Final filtered navigation:', filtered.map(i => i.name));
      return filtered;
    }
    
    // Fallback: show only 'all' items
    console.log('[AppShell] No permissions, showing only "all" items');
    const filtered = navigation.filter(item => moduleMap[item.name] === 'all');
    console.log('[AppShell] Filtered (no perms):', filtered.map(i => i.name));
    return filtered;
  }, [user, user?.module_permissions, user?.department_permissions, hasModuleAccess, hasSubmenuPermission, isAdmin, subscription, subscriptionLoaded, tenantModules]);

  const toggleMenu = (menuName, event) => {
    setActiveMenu(menuName);
    
    if (event?.currentTarget) {
      const rect = event.currentTarget.getBoundingClientRect();
      const sidebar = event.currentTarget.closest('[data-testid="desktop-sidebar"]');
      const sidebarRight = sidebar ? sidebar.getBoundingClientRect().right : rect.right;
      
      // Find the submenu item count to estimate flyout height (account for section separators)
      const navItem = badgedNavigation.find(n => n.name === menuName);
      const submenuItems = navItem?.submenu || [];
      const itemCount = submenuItems.length;
      const separatorCount = submenuItems.filter(s => s.separator).length;
      const estimatedHeight = 44 + (itemCount * 38) + (separatorCount * 26); // header + items + section labels
      const viewportHeight = window.innerHeight;
      
      // If flyout would overflow bottom, anchor from bottom up
      let top = rect.top;
      if (top + estimatedHeight > viewportHeight - 16) {
        top = Math.max(16, viewportHeight - estimatedHeight - 16);
      }
      
      setFlyoutPos({ top, left: sidebarRight + 4 });
    }
    
    setExpandedMenus(prev => {
      if (prev[menuName]) {
        return { ...prev, [menuName]: false };
      }
      const newState = {};
      Object.keys(prev).forEach(key => { newState[key] = false; });
      newState[menuName] = true;
      return newState;
    });
  };


  // Inject badge counts into navigation items
  const badgedNavigation = useMemo(() => {
    return filteredNavigation.map(item => {
      // Phase A: Mark AI-dependent items as "preparing" while ICP isn't built yet
      const isAIDependent = ['Discover Leads', 'AI Brain', 'AI Templates'].includes(item.name);
      const preparingFlag = isAIDependent && !icpReady;
      const submenu = item.submenu
        ? item.submenu.map(sub => {
            if (sub.badgeKey === 'brainAlerts' && brainAlertCount > 0) {
              return { ...sub, badge: brainAlertCount };
            }
            if (sub.badgeKey === 'responseInbox' && responseInboxCount > 0) {
              return { ...sub, badge: responseInboxCount };
            }
            return sub;
          })
        : item.submenu;
      return { ...item, submenu, preparing: preparingFlag };
    });
  }, [filteredNavigation, brainAlertCount, responseInboxCount, icpReady]);

  // Auto-highlight parent menu when child route is active (but don't auto-open flyout)
  useEffect(() => {
    let activeMenuFound = null;
    
    filteredNavigation.forEach((item) => {
      if (item.submenu) {
        const isAnySubmenuActive = item.submenu.some(
          sub => location.pathname === sub.href || location.pathname.startsWith(sub.href + '/')
        );
        if (isAnySubmenuActive) {
          activeMenuFound = item.name;
        }
      } else {
        if (location.pathname === item.href || location.pathname.startsWith(item.href + '/')) {
          activeMenuFound = item.name;
        }
      }
    });
    
    if (activeMenuFound) {
      setActiveMenu(activeMenuFound);
    }
    
    // Close all flyouts on navigation
    setExpandedMenus({});
    // NOTE: depend only on pathname. `filteredNavigation` is a new array every
    // render; including it ran this effect every render and setExpandedMenus({})
    // re-triggered a render → an infinite re-render loop that lagged the whole
    // app and starved in-page data fetches.
  }, [location.pathname]);

  // Dark mode handled by ThemeContext

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Determine if on onboarding page
  const isOnboarding = location.pathname === '/onboarding/brain-init';

  // Keep sidebar normal — always interactive
  const effectiveCollapsed = sidebarCollapsed;

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 w-64 bg-card border-r border-border">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-3" data-testid="app-logo">
                <img src="/lockene-logo.png" alt="Lockene" className="h-10 w-10 object-contain" />
                <span className="text-xl font-bold tracking-wide text-foreground">LOCKENE</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(false)} data-testid="sidebar-close-button">
                <X className="h-5 w-5" />
              </Button>
            </div>
            <MobileSidebarNav
              items={badgedNavigation}
              location={location}
              expandedMenus={expandedMenus}
              activeMenu={activeMenu}
              onToggleMenu={toggleMenu}
              onSetActiveMenu={setActiveMenu}
              onClose={() => setSidebarOpen(false)}
            />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div 
        className={`hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col transition-all duration-300 ${
          effectiveCollapsed ? 'lg:w-16' : 'lg:w-64'
        }`} 
        data-testid="app-sidebar"
      >
        <div className={`flex flex-col flex-grow border-r border-border bg-card overflow-y-auto`} data-testid="desktop-sidebar">
          <div className="flex items-center justify-between h-14 px-4 border-b border-border">
            <div className="flex items-center gap-2.5" data-testid="app-logo">
              <img src="/lockene-logo.png" alt="Lockene" className="h-8 w-8 object-contain flex-shrink-0" />
              {!effectiveCollapsed && <span className="text-lg font-bold tracking-tight text-foreground">LOCKENE</span>}
            </div>
          </div>
          <DesktopSidebarNav
            items={badgedNavigation}
            location={location}
            expandedMenus={expandedMenus}
            activeMenu={activeMenu}
            flyoutPos={flyoutPos}
            collapsed={effectiveCollapsed}
            onToggleMenu={toggleMenu}
            onSetActiveMenu={setActiveMenu}
            onCloseMenus={() => setExpandedMenus({})}
          />
          
          {/* Collapse/Expand Button */}
          <SidebarCollapseButton collapsed={effectiveCollapsed} onToggle={toggleSidebarCollapse} />
        </div>
      </div>

      {/* Main content */}
      <div className={`flex flex-col min-h-screen transition-all duration-300 ${
        effectiveCollapsed ? 'lg:pl-16' : 'lg:pl-64'
      }`}>
        {/* Top bar */}
        <header className="sticky top-0 z-40 flex h-14 items-center gap-3 border-b border-border bg-card/80 backdrop-blur-md px-4 lg:px-6" data-testid="app-header">
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
            data-testid="sidebar-open-button"
          >
            <Menu className="h-5 w-5" />
          </Button>

          <div className="flex-1" />

          {/* Header Actions Group */}
          <div className="flex items-center gap-0.5">
            {/* Theme Toggle */}
            <button
              onClick={toggleDarkMode}
              className="flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="header-theme-toggle"
              aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>

            {/* Help & Best Practices */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  data-testid="header-help-button"
                  aria-label="Help & Best Practices"
                  title="Help & Best Practices"
                >
                  <HelpCircle className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56" data-testid="header-help-menu">
                <DropdownMenuItem onClick={() => navigate('/help')} data-testid="help-menu-open-center">
                  <BookOpen className="mr-2 h-4 w-4" /> Help Center
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => window.dispatchEvent(new Event('lockene:open-docs-palette'))}
                  data-testid="help-menu-search-docs"
                >
                  <Search className="mr-2 h-4 w-4" /> Search docs
                  <span className="ml-auto text-xs tracking-widest text-muted-foreground">⌘K</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => window.dispatchEvent(new Event('lockene:replay-tour'))}
                  data-testid="help-menu-replay-tour"
                >
                  <RotateCcw className="mr-2 h-4 w-4" /> Replay product tour
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Divider */}
            <div className="hidden sm:block h-4 w-px bg-border mx-1.5" />

            {/* Credit Balance Indicator */}
            <button 
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-500/10 hover:bg-amber-500/20 transition-colors border border-amber-500/30 shadow-sm"
              onClick={() => navigate('/billing?tab=credits')}
              data-testid="header-credit-balance"
            >
              <Coins className="h-3.5 w-3.5 text-amber-500" />
              <CreditHeaderBalance />
            </button>

            {/* Divider */}
            <div className="hidden sm:block h-4 w-px bg-border mx-1.5" />

            {/* Notification Bell - Admin Only */}
            {(user?.role === 'tenant_admin' || user?.role === 'org_admin' || isAdmin()) && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 relative"
                      onClick={() => setNotificationPanelOpen(true)}
                      data-testid="notifications-bell-button"
                    >
                      <Bell className="h-4 w-4" />
                      {/* Notification badge indicator - only show if there are unread notifications */}
                      {notificationCounts.unread > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-destructive text-destructive-foreground text-xs font-medium rounded-full flex items-center justify-center">
                          {notificationCounts.unread > 99 ? '99+' : notificationCounts.unread}
                        </span>
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Notifications {notificationCounts.unread > 0 ? `(${notificationCounts.unread} unread)` : ''}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Divider before profile */}
            <div className="hidden sm:block h-4 w-px bg-border mx-1.5" />

            {/* Profile Dropdown */}
            <DropdownMenu>
            <DropdownMenuTrigger asChild data-testid="user-menu-trigger">
              <Button variant="ghost" size="sm" className="gap-1.5 px-2 h-8">
                <Avatar className="h-6 w-6">
                  {user?.profile_image ? (
                    <AvatarImage src={user.profile_image} alt={user.name} />
                  ) : (
                    <AvatarFallback className="text-[10px]">{user?.name?.charAt(0) || 'U'}</AvatarFallback>
                  )}
                </Avatar>
                <span className="hidden md:inline-block text-xs font-medium">{user?.name}</span>
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 p-1" data-testid="user-menu-content">
              {/* User Info Header */}
              <div className="px-3 py-3 border-b border-border">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    {user?.profile_image ? (
                      <AvatarImage src={user.profile_image} alt={user.name} />
                    ) : (
                      <AvatarFallback>{user?.name?.charAt(0) || 'U'}</AvatarFallback>
                    )}
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{user?.name || 'User'}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                </div>
              </div>

              {/* Finish Get Started Section - Hidden when fully complete */}
              {(() => {
                let steps = 0;
                if (onboardingCompleted) steps++;
                if (profileSetupCompleted) steps++;
                if (primaryModuleSetupDone) steps++;
                if (hasCreatedLead) steps++;
                const pct = Math.round((steps / 4) * 100);
                if (pct >= 100) return null;
                return (
                  <div 
                    className="px-3 py-3 bg-primary/5 border-b border-border cursor-pointer hover:bg-primary/10 transition-colors"
                    onClick={() => navigate('/onboarding/brain-init')}
                    data-testid="finish-getting-started"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="h-4 w-4 text-primary" />
                      <span className="text-sm font-medium">Finish Getting Started</span>
                    </div>
                    <div className="w-full bg-secondary rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all duration-300" 
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1.5">{pct}% complete</p>
                  </div>
                );
              })()}

              <div className="py-1">
                {/* Profile & Preferences */}
                <DropdownMenuItem 
                  onClick={() => navigate('/profile')} 
                  className="px-3 py-2.5 cursor-pointer focus:bg-primary/10 focus:text-primary rounded-md mx-1"
                  data-testid="user-menu-profile"
                >
                  <CircleUser className="mr-3 h-4 w-4" />
                  <span>Profile & Preferences</span>
                </DropdownMenuItem>

                {/* Theme Toggle */}
                <DropdownMenuItem 
                  onClick={toggleDarkMode}
                  className="px-3 py-2.5 cursor-pointer focus:bg-primary/10 focus:text-primary rounded-md mx-1"
                  data-testid="user-menu-theme"
                >
                  {darkMode ? (
                    <>
                      <Sun className="mr-3 h-4 w-4" />
                      <span>Switch to Light Theme</span>
                    </>
                  ) : (
                    <>
                      <Moon className="mr-3 h-4 w-4" />
                      <span>Switch to Dark Theme</span>
                    </>
                  )}
                </DropdownMenuItem>

                {/* Create User removed - use HR > Users page instead */}
              </div>

              <DropdownMenuSeparator className="my-1" />

              {/* Sign Out */}
              <DropdownMenuItem 
                onClick={handleLogout} 
                className="px-3 py-2.5 cursor-pointer focus:bg-primary/10 focus:text-primary rounded-md mx-1 mb-1"
                data-testid="user-menu-logout"
              >
                <LogOut className="mr-3 h-4 w-4" />
                <span>Sign Out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        {/* Onboarding Resume Banner - Shows when skipped and setup incomplete */}
        {onboardingSkipped && !hasCreatedLead && location.pathname !== '/onboarding/brain-init' && !bannerDismissed && (
          <div 
            className="mx-4 lg:mx-6 mt-4 rounded-xl border border-primary/20 bg-card shadow-sm relative overflow-hidden"
            data-testid="onboarding-resume-banner"
          >
            <div className="flex items-center gap-4 px-5 py-4">
              {/* Circular Progress */}
              {(() => {
                let steps = 0;
                if (onboardingCompleted) steps++; // modules selected
                if (profileSetupCompleted) steps++;
                if (primaryModuleSetupDone) steps++;
                if (hasCreatedLead) steps++;
                const pct = Math.round((steps / 4) * 100);
                const radius = 18;
                const circumference = 2 * Math.PI * radius;
                const offset = circumference - (pct / 100) * circumference;
                return (
                  <div className="relative shrink-0" data-testid="onboarding-progress-circle">
                    <svg width="48" height="48" viewBox="0 0 48 48">
                      <circle cx="24" cy="24" r={radius} fill="none" stroke="hsl(var(--muted))" strokeWidth="3" />
                      <circle
                        cx="24" cy="24" r={radius} fill="none"
                        stroke="hsl(var(--primary))" strokeWidth="3" strokeLinecap="round"
                        strokeDasharray={circumference} strokeDashoffset={offset}
                        transform="rotate(-90 24 24)"
                        className="transition-all duration-700"
                      />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center text-[11px] font-bold text-primary">
                      {pct}%
                    </span>
                  </div>
                );
              })()}

              {/* Text */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">
                  Complete setup to activate your AI assistant
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {profileSetupCompleted 
                    ? (primaryModuleSetupDone ? 'Almost there — create your first lead to finish.' : 'Set up your primary module next.') 
                    : 'Fill in your company profile to get started.'}
                </p>
              </div>

              {/* Action */}
              <Button
                size="sm"
                variant="default"
                onClick={handleResumeOnboarding}
                className="h-8 text-xs px-4 shrink-0"
                data-testid="resume-onboarding-btn"
              >
                Continue Setup
              </Button>

              {/* Dismiss */}
              <button
                onClick={() => setBannerDismissed(true)}
                className="absolute top-2 right-2 p-1 rounded-full hover:bg-muted transition-colors"
                data-testid="dismiss-banner-btn"
              >
                <X className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </div>
          </div>
        )}

        {/* Credits Running Low Banner */}
        <CreditsLowBanner />
        <DemoModeBanner />

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6 relative overflow-x-hidden" data-testid="app-content">
          <SectionErrorBoundary section="Activation Banner">
            <ActivationBanner />
          </SectionErrorBoundary>
          <SectionErrorBoundary section="Page Content">
            <AIGate>
              {children}
            </AIGate>
          </SectionErrorBoundary>
        </main>
      </div>

      {/* Global background removed */}

      {/* Notification Panel */}
      <NotificationPanel
        open={notificationPanelOpen}
        onOpenChange={setNotificationPanelOpen}
        notifications={notifications}
        notificationCounts={notificationCounts}
        notificationsLoading={notificationsLoading}
        onMarkAsRead={markAsRead}
        onMarkAllAsRead={markAllAsRead}
      />

      {/* First-login guided tour (plan/role-aware, always skippable) */}
      <FirstLoginTour
        planTier={subscription?.plan_id || user?.plan_id || 'free'}
        isAdmin={user?.role === 'tenant_admin' || user?.role === 'org_admin' || isAdmin()}
      />

      <BrainNotificationListener />
      <GlobalCreditListener />
      <DocsCommandPalette />
    </div>
  );
};
